import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Navigation, ChevronLeft, MapPin, Clock, Euro, Loader2,
  Car, ExternalLink, Play, CheckCircle, AlertCircle, Smartphone
} from 'lucide-react';

const NewTripPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    start_address: '',
    end_address: '',
    distance_km: '',
    duration_minutes: '',
    amount: '',
    client_name: '',
    vehicle_id: '',
    notes: ''
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setMounted(true);
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    const token = localStorage.getItem('optitrajet_token');
    try {
      const res = await fetch(`${API_URL}/api/optitrajet/fleet/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVehicles(data.vehicles || []);
      }
    } catch (err) {
      console.error('Fetch vehicles error:', err);
    }
  };

  const openExternalGPS = () => {
    const destination = formData.end_address;
    if (!destination) {
      setError('Veuillez entrer une destination');
      return;
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const encodedDest = encodeURIComponent(destination);
    const encodedStart = formData.start_address ? encodeURIComponent(formData.start_address) : '';

    if (isIOS) {
      // Apple Maps with start and destination
      if (encodedStart) {
        window.open(`maps://maps.apple.com/?saddr=${encodedStart}&daddr=${encodedDest}`, '_blank');
      } else {
        window.open(`maps://maps.apple.com/?daddr=${encodedDest}`, '_blank');
      }
    } else if (isAndroid) {
      // Google Maps on Android
      window.open(`google.navigation:q=${encodedDest}`, '_blank');
    } else {
      // Fallback to Google Maps web
      if (encodedStart) {
        window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodedStart}&destination=${encodedDest}`, '_blank');
      } else {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedDest}`, '_blank');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('optitrajet_token');
    
    try {
      const response = await fetch(`${API_URL}/api/optitrajet/trips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          distance_km: parseFloat(formData.distance_km) || 0,
          duration_minutes: parseInt(formData.duration_minutes) || 0,
          amount: parseFloat(formData.amount) || 0,
          status: 'completed'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la création');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/optitrajet/trips');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate amount based on distance and vehicle rate
  useEffect(() => {
    if (formData.distance_km && formData.vehicle_id) {
      const vehicle = vehicles.find(v => v.id === formData.vehicle_id);
      if (vehicle) {
        const amount = (parseFloat(formData.distance_km) * vehicle.km_rate).toFixed(2);
        setFormData(prev => ({ ...prev, amount }));
      }
    }
  }, [formData.distance_km, formData.vehicle_id, vehicles]);

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#F8FAFC' }}>
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 mb-2">
              Trajet enregistré !
            </h2>
            <p className="text-slate-500">Redirection en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
          <button 
            onClick={() => navigate('/optitrajet/trips')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 ml-2">
            Nouveau trajet
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* GPS Button - Main CTA */}
        <Card 
          className={`mb-6 border-0 shadow-xl overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 50%, #7C3AED 100%)' }}
        >
          <CardContent className="p-6">
            <div className="text-center text-white">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8" />
              </div>
              <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-semibold mb-2">
                Lancer la navigation
              </h2>
              <p className="text-blue-100 text-sm mb-4">
                Entrez votre destination ci-dessous puis lancez Google Maps ou Apple Maps
              </p>
              <Button
                type="button"
                onClick={openExternalGPS}
                size="lg"
                className="bg-white hover:bg-slate-50 text-blue-700 font-semibold shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 rounded-xl h-14 px-8"
                data-testid="launch-gps-btn"
              >
                <ExternalLink className="h-5 w-5 mr-2" />
                Ouvrir le GPS
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 flex items-center gap-3 animate-in slide-in-from-top duration-300">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Trip Form */}
        <Card className={`border-0 shadow-lg transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-6">
            <h3 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-4">
              Détails du trajet
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Addresses */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse de départ</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                    <Input
                      placeholder="Ex: 1 Avenue des Champs-Élysées, Paris"
                      value={formData.start_address}
                      onChange={(e) => setFormData({ ...formData, start_address: e.target.value })}
                      className="pl-12 h-12 rounded-xl border-slate-200"
                      required
                      data-testid="start-address"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse d'arrivée</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                    <Input
                      placeholder="Ex: Aéroport Charles de Gaulle"
                      value={formData.end_address}
                      onChange={(e) => setFormData({ ...formData, end_address: e.target.value })}
                      className="pl-12 h-12 rounded-xl border-slate-200"
                      required
                      data-testid="end-address"
                    />
                  </div>
                </div>
              </div>

              {/* Distance & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Distance (km)</label>
                  <div className="relative">
                    <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="0"
                      value={formData.distance_km}
                      onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                      className="pl-12 h-12 rounded-xl border-slate-200"
                      data-testid="distance"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Durée (min)</label>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      type="number"
                      placeholder="0"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                      className="pl-12 h-12 rounded-xl border-slate-200"
                      data-testid="duration"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle selection */}
              {vehicles.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Véhicule</label>
                  <div className="relative">
                    <Car className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <select
                      value={formData.vehicle_id}
                      onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                      className="w-full pl-12 h-12 rounded-xl border border-slate-200 bg-white text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      data-testid="vehicle-select"
                    >
                      <option value="">Sélectionner un véhicule</option>
                      {vehicles.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.plate_number}) - {v.km_rate}€/km
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Montant (€)</label>
                <div className="relative">
                  <Euro className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="pl-12 h-12 rounded-xl border-slate-200"
                    data-testid="amount"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Calculé automatiquement si véhicule sélectionné
                </p>
              </div>

              {/* Client name (optional) */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom du client (optionnel)</label>
                <Input
                  placeholder="Ex: Société ABC"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="h-12 rounded-xl border-slate-200"
                  data-testid="client-name"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Notes (optionnel)</label>
                <textarea
                  placeholder="Notes sur ce trajet..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full p-4 rounded-xl border border-slate-200 resize-none h-24 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  data-testid="notes"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg transition-all hover:shadow-xl"
                data-testid="save-trip-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Enregistrer le trajet
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NewTripPage;
